import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

// Trending Global (2026-10-18): video yang sedang naik di YouTube semua negara + niche global (musik & non-musik).
// Backend: routes/trending.js + lib/trend-query.js di starhub-creative-api; data dikumpulkan
// trend-collector.js tiap 6 jam memakai YouTube Data API key. Halaman ini hanya membaca DB.
// Sengaja TANPA tombol repost: video trending niche global hampir pasti dilindungi Content ID.
const API = 'https://api.starhubcreative.id';
const PAGE = 48;

const fmt = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '-';
  if (n >= 1e9) return (n / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' M';
  if (n >= 1e6) return (n / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' jt';
  if (n >= 1e3) return (n / 1e3).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' rb';
  return n.toLocaleString('id-ID');
};
const dur = (s) => {
  if (s == null) return '';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), d = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(d).padStart(2, '0')}` : `${m}:${String(d).padStart(2, '0')}`;
};
const umur = (iso) => {
  if (!iso) return '';
  const h = (Date.now() - new Date(iso).getTime()) / 3600e3;
  if (h < 1) return 'baru saja';
  if (h < 48) return `${Math.round(h)} jam lalu`;
  const d = Math.round(h / 24);
  return d < 60 ? `${d} hari lalu` : `${Math.round(d / 30)} bulan lalu`;
};
let regionNames = null, langNames = null;
try { regionNames = new Intl.DisplayNames(['id'], { type: 'region' }); langNames = new Intl.DisplayNames(['id'], { type: 'language' }); } catch (e) { /* peramban lama */ }
const namaNegara = (code, fallback) => { try { return (regionNames && regionNames.of(code)) || fallback || code; } catch (e) { return fallback || code; } };
const namaBahasa = (code) => { try { return (langNames && langNames.of(code)) || code; } catch (e) { return code; } };
const bendera = (code) => /^[A-Z]{2}$/.test(code || '') ? String.fromCodePoint(...[...code].map(c => 0x1F1A5 + c.charCodeAt(0))) : '';

const TIER = {
  tinggi: { t: 'Pasar tinggi', c: colors.success, bg: colors.successMuted },
  sedang: { t: 'Pasar sedang', c: colors.warning, bg: colors.warningMuted },
  rendah: { t: 'Pasar rendah', c: colors.textSecondary, bg: colors.bgCardMuted },
};
const SORT_OPTS = {
  chart: [['laju', 'Paling cepat naik'], ['outlier', 'Channel kecil meledak'], ['peringkat', 'Peringkat chart'], ['negara', 'Trending di banyak negara'], ['nilai', 'Nilai pasar'], ['views', 'Views terbanyak'], ['baru', 'Terbaru']],
  niche: [['laju', 'Paling cepat naik'], ['outlier', 'Channel kecil meledak'], ['nilai', 'Nilai pasar'], ['views', 'Views terbanyak'], ['baru', 'Terbaru']],
};

const sel = { padding: '8px 10px', fontSize: 13, borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, minWidth: 0, maxWidth: '100%' };
const label = { fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: 'block' };
const badge = (c, bg) => ({ fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 6, background: bg, color: c, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' });
const btn = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 };

function Field({ title, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 150px', minWidth: 0 }}><span style={label}>{title}</span>{children}</div>;
}

// Cek bahasa komentar (2026-10-19): ±100 komentar teratas → sebaran bahasa + negara yang disebut penonton.
// Hasil disimpan server 7 hari & dipakai bersama; klik ulang tidak memakan biaya.
function BahasaKomentar({ videoId }) {
  const [d, setD] = useState(undefined); // undefined = memuat, null = belum pernah dicek
  const [jalan, setJalan] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { axios.get(`${API}/api/trending/videos/${videoId}/bahasa-komentar`).then(r => setD(r.data)).catch(() => setD(null)); }, [videoId]);
  const cek = async () => {
    setJalan(true); setErr('');
    try { const r = await axios.post(`${API}/api/trending/videos/${videoId}/bahasa-komentar`); setD(r.data); }
    catch (e) { setErr(e.response?.data?.error || 'Gagal menganalisa komentar'); }
    setJalan(false);
  };
  const h = d && d.hasil;
  const terbaca = h ? (h.dihitung ?? d.sampel) : 0;
  const warna = [colors.accent, colors.success, colors.warning, '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#64748B', colors.textMuted];
  return (
    <div style={{ marginTop: 14, borderTop: `1px solid ${colors.border}`, paddingTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}><i className="ti ti-message-language" style={{ color: colors.accent }}></i> Bahasa komentar</div>
        {d !== undefined && (
          <button style={btn} disabled={jalan} onClick={cek}>
            <i className={`ti ${jalan ? 'ti-loader-2' : 'ti-language'}`} style={{ fontSize: 14 }}></i>
            {jalan ? 'Membaca komentar…' : d ? 'Cek ulang' : 'Cek bahasa komentar'}
          </button>
        )}
      </div>
      {err && <div style={{ color: colors.danger, fontSize: 12, marginTop: 6 }}>{err}</div>}
      {d === null && !jalan && <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Baca ±100 komentar teratas untuk melihat bahasa penonton dan negara yang mereka sebut. Petunjuk tambahan, bukan data pasti.</div>}
      {d && d.status === 'disabled' && <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Komentar video ini dimatikan pemiliknya.</div>}
      {d && d.status === 'kosong' && <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Komentarnya terlalu sedikit ({d.sampel}) untuk dianalisa.</div>}
      {h && (
        <div style={{ marginTop: 8 }}>
          {terbaca < 10 && <div style={{ fontSize: 12, color: colors.warning, marginBottom: 6 }}><i className="ti ti-alert-triangle"></i> Hanya {terbaca} komentar yang bahasanya terbaca — data terlalu sedikit untuk dijadikan patokan.</div>}
          <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: colors.bgCardMuted, marginBottom: 8 }}>
            {h.bahasa.map((b, i) => <div key={b.kode + i} title={`${b.nama} ${b.persen}%`} style={{ width: `${b.persen}%`, background: warna[i % warna.length] }} />)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 12, color: colors.textPrimary }}>
            {h.bahasa.map((b, i) => <span key={b.kode + i}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: warna[i % warna.length], marginRight: 4 }} />{b.nama} {b.persen}%</span>)}
          </div>
          {h.petunjukNegara && h.petunjukNegara.length > 0 && (
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Penonton menyebut asalnya: {h.petunjukNegara.map(n => `${bendera(n.negara)} ${namaNegara(n.negara)}${n.jumlah > 1 ? ` (${n.jumlah})` : ''}`).join(', ')}</div>
          )}
          {h.catatan && <div style={{ fontSize: 12, color: colors.textPrimary, marginTop: 6 }}>{h.catatan}</div>}
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>Dari {d.sampel} komentar teratas · dicek {umur(d.created_at)}</div>
        </div>
      )}
    </div>
  );
}

// Salin teks ke clipboard dengan cadangan untuk peramban lama.
async function salinTeks(t) {
  try { await navigator.clipboard.writeText(t); return true; } catch (e) {
    try { const el = document.createElement('textarea'); el.value = t; document.body.appendChild(el); el.select(); document.execCommand('copy'); el.remove(); return true; } catch (e2) { return false; }
  }
}
function TombolSalin({ teks, label = 'Salin' }) {
  const [ok, setOk] = useState(false);
  return (
    <button style={{ ...btn, padding: '4px 8px', fontSize: 11 }} disabled={!teks}
      onClick={async () => { if (await salinTeks(teks)) { setOk(true); setTimeout(() => setOk(false), 2000); } }}>
      <i className={`ti ${ok ? 'ti-check' : 'ti-copy'}`} style={{ fontSize: 13 }}></i>{ok ? 'Tersalin' : label}
    </button>
  );
}
const judulBagian = { fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' };
const kotakBagian = { borderTop: `1px solid ${colors.border}`, paddingTop: 10, marginTop: 10 };
function KepalaBagian({ judul, kanan }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}><span style={judulBagian}>{judul}</span>{kanan}</div>;
}

// SEO & Tags (2026-10-19): judul, deskripsi lengkap, semua tags, hashtag, kategori, topik, judul bahasa lain.
function SeoTab({ videoId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  // Judul/deskripsi/tags bisa diedit sebelum disalin (2026-10-22); tombol Salin memakai versi editan.
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [tagsText, setTagsText] = useState('');
  const isi = data => { setTitle(data.title || ''); setDesc(data.description || ''); setTagsText(data.tags_text || ''); };
  useEffect(() => { axios.get(`${API}/api/trending/videos/${videoId}/seo`).then(r => { setD(r.data); isi(r.data); }).catch(e => setErr(e.response?.data?.error || 'Gagal memuat data SEO')); }, [videoId]);
  if (err) return <div style={{ color: colors.danger, fontSize: 13, marginTop: 10 }}>{err}</div>;
  if (!d) return <div style={{ color: colors.textSecondary, fontSize: 13, marginTop: 10 }}>Mengambil judul, deskripsi & tags…</div>;
  const tagList = tagsText.split(',').map(t => t.trim()).filter(Boolean);
  const tagsBersih = tagList.join(', ');
  const diubah = title !== (d.title || '') || desc !== (d.description || '') || tagsText !== (d.tags_text || '');
  const semua = `${title}\n\n${desc}\n\nTags: ${tagsBersih}`;
  const field = { width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit', color: colors.textPrimary, background: colors.bgCardMuted, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '8px 10px' };
  const chipSm = { fontSize: 11, padding: '3px 8px', borderRadius: 6, background: colors.bgCardMuted, border: `1px solid ${colors.border}`, color: colors.textPrimary };
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: colors.textSecondary }}>
          Edit dulu sesuai kontenmu, lalu salin.{diubah && <> · <span onClick={() => isi(d)} style={{ color: colors.accentText, cursor: 'pointer', textDecoration: 'underline' }}>Kembalikan ke asli</span></>}
        </span>
        <TombolSalin teks={semua} label="Salin semua (judul + deskripsi + tags)" />
      </div>
      <div style={kotakBagian}>
        <KepalaBagian judul={`Judul · ${title.length}/100 karakter`} kanan={<TombolSalin teks={title} />} />
        <input value={title} onChange={e => setTitle(e.target.value)} style={{ ...field, fontWeight: 600, borderColor: title.length > 100 ? colors.danger : colors.border }} />
      </div>
      <div style={kotakBagian}>
        <KepalaBagian judul={`Deskripsi · ${desc.length} karakter`} kanan={<TombolSalin teks={desc} />} />
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={8} placeholder="Tanpa deskripsi — tulis sendiri di sini." style={{ ...field, fontSize: 12, lineHeight: 1.5, resize: 'vertical', minHeight: 120 }} />
      </div>
      <div style={kotakBagian}>
        <KepalaBagian judul={`Tags · ${tagList.length} tag · ${tagsBersih.length}/500 karakter`} kanan={<TombolSalin teks={tagsBersih} label="Salin tags" />} />
        <textarea value={tagsText} onChange={e => setTagsText(e.target.value)} rows={3} placeholder="tag1, tag2, tag3 (pisah koma)" style={{ ...field, fontSize: 12, lineHeight: 1.5, resize: 'vertical', borderColor: tagsBersih.length > 500 ? colors.danger : colors.border }} />
        {d.tags.length === 0 && <div style={{ fontSize: 11.5, color: colors.textSecondary, marginTop: 4 }}>Pemilik video tidak memasang tags — isi sendiri bila perlu.</div>}
      </div>
      <div style={kotakBagian}>
        <KepalaBagian judul={`Hashtag · ${d.hashtags.length}`} kanan={d.hashtags.length ? <TombolSalin teks={d.hashtags.join(' ')} label="Salin hashtag" /> : null} />
        {d.hashtags.length
          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{d.hashtags.map(h => <span key={h} style={{ ...chipSm, color: colors.accentText }}>{h}</span>)}</div>
          : <div style={{ fontSize: 12, color: colors.textSecondary }}>Tidak ada hashtag di judul/deskripsi.</div>}
      </div>
      <div style={{ ...kotakBagian, fontSize: 12, color: colors.textPrimary, display: 'grid', gap: 4 }}>
        <div><span style={{ color: colors.textSecondary }}>Kategori:</span> {d.category || '-'}</div>
        {d.topics.length > 0 && <div><span style={{ color: colors.textSecondary }}>Topik (dari YouTube):</span> {d.topics.join(', ')}</div>}
        {(d.lang || d.audio_lang) && <div><span style={{ color: colors.textSecondary }}>Bahasa metadata / audio:</span> {d.lang ? namaBahasa(d.lang.split('-')[0]) : '-'} / {d.audio_lang && !/^(zxx|und|mul)$/i.test(d.audio_lang.split('-')[0]) ? namaBahasa(d.audio_lang.split('-')[0]) : '-'}</div>}
      </div>
      {d.localizations.length > 0 && (
        <div style={kotakBagian}>
          <KepalaBagian judul={`Judul versi bahasa lain · ${d.localizations.length}`} />
          <div style={{ display: 'grid', gap: 4 }}>
            {d.localizations.map(l => (
              <div key={l.lang} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span style={{ ...chipSm, minWidth: 34, textAlign: 'center' }}>{l.lang}</span>
                <span style={{ flex: 1, color: colors.textPrimary }}>{l.title}</span>
                <TombolSalin teks={l.title} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 10 }}>Data dari YouTube · diambil {umur(d.fetched_at)}. Pakai sebagai acuan pola; jangan menyalin judul/deskripsi persis milik orang lain.</div>
    </div>
  );
}

// Ambil audio (2026-10-19): MP3 192 kbps diambil server, bisa diunduh 24 jam.
function AudioTab({ videoId, video }) {
  const [st, setSt] = useState(undefined);
  const [err, setErr] = useState('');
  const [unduh, setUnduh] = useState(false);
  const muat = useCallback(() => axios.get(`${API}/api/trending/videos/${videoId}/audio`).then(r => setSt(r.data)).catch(() => setSt(null)), [videoId]);
  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    if (!st || st.status !== 'proses') return undefined;
    const t = setInterval(muat, 4000); return () => clearInterval(t);
  }, [st, muat]);
  const mulai = async () => {
    setErr('');
    try { const r = await axios.post(`${API}/api/trending/videos/${videoId}/audio`); setSt(r.data); }
    catch (e) { setErr(e.response?.data?.error || 'Gagal memulai'); }
  };
  const simpan = async () => {
    setUnduh(true); setErr('');
    try {
      const r = await axios.get(`${API}/api/trending/videos/${videoId}/audio/file`, { responseType: 'blob', timeout: 0 });
      const nama = String(video?.title || videoId).replace(/[\\/:*?"<>|\r\n]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100) || videoId;
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `${nama}.mp3`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch (e) { setErr('Gagal mengunduh berkas. Coba lagi.'); }
    setUnduh(false);
  };
  const terlaluPanjang = video?.duration_sec > 3600;
  return (
    <div style={{ marginTop: 8 }}>
      {video?.license === 'creativeCommon' ? (
        <div style={{ background: colors.successMuted, color: colors.success, borderRadius: radius.md, padding: 10, fontSize: 12, marginBottom: 10 }}>
          <i className="ti ti-license"></i> <b>Creative Commons</b> — pemilik mengizinkan dipakai ulang dengan mencantumkan sumber (nama channel + link video).
        </div>
      ) : video?.licensed ? (
        <div style={{ background: colors.dangerMuted, color: colors.danger, borderRadius: radius.md, padding: 10, fontSize: 12, marginBottom: 10 }}>
          <i className="ti ti-alert-triangle"></i> <b>Video ini ber-Content ID.</b> Audionya terdaftar milik label/pemilik hak. Kalau diunggah ulang, video akan diklaim (iklan dialihkan ke pemilik) atau kena teguran hak cipta. Pakai hanya untuk referensi, atau bila kamu punya hak/lisensinya.
        </div>
      ) : (
        <div style={{ background: colors.warningMuted, color: colors.warning, borderRadius: radius.md, padding: 10, fontSize: 12, marginBottom: 10 }}>
          <i className="ti ti-info-circle"></i> Video tidak diklaim partner Content ID, tapi musik di dalamnya tetap bisa milik orang lain. Pastikan kamu punya hak sebelum memakai audio ini di konten yang diunggah.
        </div>
      )}
      {err && <div style={{ color: colors.danger, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {st === undefined ? <div style={{ fontSize: 13, color: colors.textSecondary }}>Memuat…</div>
        : terlaluPanjang ? <div style={{ fontSize: 13, color: colors.textSecondary }}>Video lebih dari 60 menit — audio tidak bisa diambil.</div>
        : st && st.status === 'siap' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button style={{ ...btn, background: colors.accent, color: '#fff', borderColor: colors.accent, padding: '8px 14px' }} disabled={unduh} onClick={simpan}>
              <i className={`ti ${unduh ? 'ti-loader-2' : 'ti-download'}`} style={{ fontSize: 15 }}></i>{unduh ? 'Mengunduh…' : 'Unduh MP3'}
            </button>
            <span style={{ fontSize: 12, color: colors.textSecondary }}>{(Number(st.size) / 1048576).toLocaleString('id-ID', { maximumFractionDigits: 1 })} MB · {dur(st.durasi)} · 192 kbps · tersedia sampai {new Date(st.berlaku_sampai).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' })}</span>
          </div>
        ) : st && st.status === 'proses' ? (
          <div style={{ fontSize: 13, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-loader-2" style={{ fontSize: 16, color: colors.accent }}></i> Sedang mengambil audio… biasanya 20–60 detik. Panel ini boleh ditutup, prosesnya tetap jalan.
          </div>
        ) : (
          <div>
            {st && st.status === 'gagal' && <div style={{ fontSize: 12, color: colors.danger, marginBottom: 8 }}>{st.error || 'Gagal mengambil audio.'}</div>}
            <button style={{ ...btn, padding: '8px 14px' }} onClick={mulai}>
              <i className="ti ti-music" style={{ fontSize: 15 }}></i>{st && st.status === 'gagal' ? 'Coba lagi' : 'Ambil audio (MP3)'}
            </button>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>Maksimal 20 audio per hari. Berkas disimpan server 24 jam.</div>
          </div>
        )}
    </div>
  );
}

// Detail geografi satu video (2026-10-19). Asal penonton pasti hanya bisa dilihat pemilik channel
// (YouTube Analytics); di sini petunjuk terbaik dari data publik: chart negara + negara asal channel.
// Buat proyek UGC dari video ini (2026-10-22). Khusus video panjang; audio hanya untuk Creative Commons
// tanpa klaim Content ID (aturan sama di server: POST /api/trending/videos/:id/ugc).
function BuatUgc({ video, onOpenUgc }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const shorts = video.duration_sec != null && video.duration_sec <= 180 && (video.vertical === true || (video.vertical == null && video.is_short === true));
  if (shorts) return null;
  const cc = video.license === 'creativeCommon' && video.licensed !== true;
  const buat = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/trending/videos/${video.video_id}/ugc`);
      const d = r.data;
      const note = d.audio === 'ya'
        ? 'Proyek dibuat dari video Trending (Creative Commons). Audionya sedang disiapkan sebagai lagu; kredit pemilik otomatis masuk ke deskripsi. Tambah lagu lain, pilih latar & channel, lalu proses.'
        : d.audio === 'url_mati' ? 'Proyek dibuat tanpa lagu: tambah lagu dari URL tidak aktif untuk akun ini, jadi audio CC tidak bisa dimasukkan. Unggah lagu sendiri, lalu proses.'
        : d.audio === 'terlalu_panjang' ? 'Proyek dibuat tanpa lagu: video acuan lebih dari 2 jam. Tambahkan lagu sendiri, lalu proses.'
        : 'Proyek dibuat tanpa lagu. Judul & tags video Trending dipakai sebagai acuan AI saat membuat judul. Tambahkan lagu yang hak-nya kamu pegang, lalu proses.';
      onOpenUgc(d.id, note);
    } catch (e) { setErr(e.response?.data?.error || 'Gagal membuat proyek UGC'); setBusy(false); }
  };
  return (
    <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: radius.md, border: `1px solid ${cc ? colors.success : colors.border}`, background: cc ? colors.successMuted : colors.bgCardMuted, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
        {cc
          ? <><b style={{ color: colors.success }}>Creative Commons</b> — audio video ini ikut dimasukkan sebagai lagu, dan kredit pemilik otomatis ditambahkan ke deskripsi.</>
          : <>Audio <b>tidak</b> diambil: lagu di video ini tetap milik pemiliknya (tanpa Content ID ≠ bebas dipakai). Proyek dibuat tanpa lagu; judul & tags video ini jadi acuan AI.</>}
      </div>
      <button onClick={buat} disabled={busy} style={{ ...btn, background: colors.accent, color: '#fff', borderColor: colors.accent, padding: '7px 12px' }}>
        <i className={`ti ${busy ? 'ti-loader-2' : 'ti-movie'}`} style={{ fontSize: 15 }}></i>{busy ? 'Membuat…' : 'Buat proyek UGC'}
      </button>
      {err && <div style={{ width: '100%', fontSize: 12, color: colors.danger }}>{err}</div>}
    </div>
  );
}

function GeoPanel({ videoId, categories, onClose, tabAwal = 'seo', ugcOn, onOpenUgc }) {
  const [tab, setTab] = useState(tabAwal);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/trending/videos/${videoId}/negara`).then(r => setD(r.data)).catch(e => setErr(e.response?.data?.error || 'Gagal memuat'));
  }, [videoId]);
  useEffect(() => { const k = e => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const katLabel = id => (categories || []).find(c => c.id === String(id))?.label || `Kategori ${id}`;
  const masih = d ? d.negara.filter(n => n.masih) : [];
  const keluar = d ? d.negara.filter(n => !n.masih) : [];
  const perTier = { tinggi: 0, sedang: 0, rendah: 0 };
  masih.forEach(n => { perTier[(n.rpm ?? 0.5) >= 3 ? 'tinggi' : (n.rpm ?? 0.5) >= 1 ? 'sedang' : 'rendah']++; });
  const lama = (a, b) => { const h = (new Date(b) - new Date(a)) / 3600e3; return h < 1 ? 'baru masuk' : h < 48 ? `±${Math.round(h)} jam` : `±${Math.round(h / 24)} hari`; };
  const Baris = ({ n }) => {
    const gerak = n.rank_awal != null && n.rank_terakhir != null ? n.rank_awal - n.rank_terakhir : 0;
    return (
      <tr style={{ borderTop: `1px solid ${colors.border}`, opacity: n.masih ? 1 : 0.55 }}>
        <td style={{ padding: '7px 6px', whiteSpace: 'nowrap' }}>{bendera(n.code)} {namaNegara(n.code, n.name)}</td>
        <td style={{ padding: '7px 6px', whiteSpace: 'nowrap' }}>
          {n.rank_terakhir != null ? <>#{n.rank_terakhir}{gerak !== 0 && <span style={{ color: gerak > 0 ? colors.success : colors.danger, fontSize: 11, marginLeft: 4 }}>{gerak > 0 ? `▲${gerak}` : `▼${-gerak}`}</span>}</> : <span style={{ color: colors.textMuted }}>—</span>}
        </td>
        <td style={{ padding: '7px 6px' }}>
          {Object.entries(n.rank_kategori || {}).map(([k, r]) => <span key={k} style={{ ...badge(colors.accentText, colors.accentMuted), marginRight: 4 }}>#{r} {katLabel(k)}</span>)}
        </td>
        <td style={{ padding: '7px 6px', whiteSpace: 'nowrap', color: colors.textSecondary }}>{n.masih ? lama(n.pertama, n.terakhir) : `keluar ${umur(n.terakhir)}`}</td>
      </tr>
    );
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: colors.bgCard, borderRadius: radius.lg, width: '100%', maxWidth: 680, padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}><i className="ti ti-list-details" style={{ color: colors.accent }}></i> Detail video</div>
          <button onClick={onClose} aria-label="Tutup" style={{ ...btn, padding: '4px 8px' }}><i className="ti ti-x"></i></button>
        </div>
        {err && <div style={{ color: colors.danger, fontSize: 13, marginTop: 10 }}>{err}</div>}
        {!d && !err && <div style={{ color: colors.textSecondary, fontSize: 13, marginTop: 10 }}>Memuat…</div>}
        {d && (
          <>
            <div style={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600, marginTop: 8 }}>{d.video.title}</div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
              {d.video.channel_title} · Channel dari: {d.video.channel_country ? `${bendera(d.video.channel_country)} ${namaNegara(d.video.channel_country)}` : 'tidak dicantumkan'}
              {d.video.lang && !/^(zxx|und|mul)$/i.test(String(d.video.lang).split('-')[0]) ? ` · Bahasa: ${namaBahasa(String(d.video.lang).split('-')[0])}` : ''}
            </div>
            {ugcOn && onOpenUgc && <BuatUgc video={d.video} onOpenUgc={onOpenUgc} />}
            <div style={{ display: 'flex', gap: 6, margin: '12px 0 4px', flexWrap: 'wrap' }}>
              {[['seo', 'ti-file-description', 'SEO & Tags'], ['audio', 'ti-music', 'Audio'], ['geo', 'ti-world', 'Geografi']].map(([k, ic, t]) => (
                <button key={k} onClick={() => setTab(k)}
                  style={{ ...btn, padding: '6px 12px', background: tab === k ? colors.accentMuted : colors.bgCard, color: tab === k ? colors.accentText : colors.textSecondary, borderColor: tab === k ? colors.accent : colors.border }}>
                  <i className={`ti ${ic}`} style={{ fontSize: 14 }}></i>{t}
                </button>
              ))}
            </div>
            {tab === 'seo' && <SeoTab videoId={videoId} />}
            {tab === 'audio' && <AudioTab videoId={videoId} video={d.video} />}
            {tab === 'geo' && <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' }}>
              <div style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: '8px 12px' }}><div style={{ fontSize: 11, color: colors.textSecondary }}>Masih trending</div><div style={{ fontSize: 16, fontWeight: 700 }}>{masih.length} negara</div></div>
              <div style={{ background: colors.successMuted, borderRadius: radius.md, padding: '8px 12px' }}><div style={{ fontSize: 11, color: colors.success }}>Pasar tinggi</div><div style={{ fontSize: 16, fontWeight: 700, color: colors.success }}>{perTier.tinggi}</div></div>
              <div style={{ background: colors.warningMuted, borderRadius: radius.md, padding: '8px 12px' }}><div style={{ fontSize: 11, color: colors.warning }}>Pasar sedang</div><div style={{ fontSize: 16, fontWeight: 700, color: colors.warning }}>{perTier.sedang}</div></div>
              <div style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: '8px 12px' }}><div style={{ fontSize: 11, color: colors.textSecondary }}>Pasar rendah</div><div style={{ fontSize: 16, fontWeight: 700 }}>{perTier.rendah}</div></div>
              {keluar.length > 0 && <div style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: '8px 12px' }}><div style={{ fontSize: 11, color: colors.textSecondary }}>Sudah keluar</div><div style={{ fontSize: 16, fontWeight: 700 }}>{keluar.length}</div></div>}
            </div>
            {d.negara.length === 0 ? (
              <div style={{ fontSize: 13, color: colors.textSecondary, padding: '8px 0' }}>
                Video ini belum masuk chart negara mana pun{d.niche.length ? `. Ditemukan lewat pencarian niche: ${d.niche.join(', ')}` : ''}.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: colors.textPrimary }}>
                  <thead><tr style={{ textAlign: 'left', color: colors.textSecondary, background: colors.bgCardMuted, position: 'sticky', top: 0 }}>
                    <th style={{ padding: '7px 6px' }}>Negara</th><th style={{ padding: '7px 6px' }} title="Peringkat di chart umum (semua kategori); panah = naik/turun sejak pertama terlihat">Chart umum</th>
                    <th style={{ padding: '7px 6px' }}>Chart kategori</th><th style={{ padding: '7px 6px' }}>Lama trending</th>
                  </tr></thead>
                  <tbody>{[...masih, ...keluar].map(n => <Baris key={n.code} n={n} />)}</tbody>
                </table>
              </div>
            )}
            <BahasaKomentar videoId={videoId} />
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 10, lineHeight: 1.5 }}>
              <i className="ti ti-info-circle"></i> Asal penonton yang pasti hanya bisa dilihat pemilik channel (YouTube Studio). Negara tempat video masuk chart adalah petunjuk terkuat: chart tiap negara dihitung dari penonton di negara itu. "Channel dari" diisi sendiri oleh pemilik channel.
            </div>
            </>}
          </>
        )}
      </div>
    </div>
  );
}

function VideoCard({ v, mode, onSave, onDna, dnaOn, busy, onGeo }) {
  const yt = `https://www.youtube.com/watch?v=${v.video_id}`;
  const tier = TIER[v.tier] || TIER.rendah;
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <a href={yt} target="_blank" rel="noreferrer" style={{ position: 'relative', display: 'block', aspectRatio: '16 / 9', background: colors.bgCardMuted }}>
        <img src={v.thumbnail} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        {v.duration_sec != null && <span style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,0.78)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4 }}>{dur(v.duration_sec)}</span>}
        {v.is_short && <span style={{ position: 'absolute', left: 6, bottom: 6, background: '#FF0033', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{v.vertical === true ? 'SHORTS' : 'SHORTS?'}</span>}
        {mode === 'chart' && v.best_rank && <span style={{ position: 'absolute', left: 6, top: 6, background: 'rgba(0,0,0,0.78)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>#{v.best_rank}</span>}
      </a>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        <a href={yt} target="_blank" rel="noreferrer" title={v.title}
          style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, textDecoration: 'none', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{v.title}</a>
        <div style={{ fontSize: 12, color: colors.textSecondary }}>
          {v.channel_title}{v.channel_country ? ` ${bendera(v.channel_country)}` : ''}{v.channel_subs != null ? ` · ${fmt(v.channel_subs)} subscriber` : ''}
          <br />{fmt(v.views)} views · {umur(v.published_at)}{v.lang && !/^(zxx|und|mul)$/i.test(String(v.lang).split('-')[0]) ? ` · ${namaBahasa(String(v.lang).split('-')[0])}` : ''}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          <span style={badge(colors.accentText, colors.accentMuted)} title={v.laju_nyata ? 'Dihitung dari pertambahan views antar pengambilan data' : 'Baru terlihat sekali: rata-rata views per jam sejak tayang'}>
            <i className="ti ti-rocket" style={{ fontSize: 13 }}></i>+{fmt(v.per_hour)}/jam{v.laju_nyata ? '' : '*'}
          </span>
          {v.outlier != null && v.outlier >= 1 && (
            <span style={badge(v.outlier >= 5 ? colors.danger : colors.warning, v.outlier >= 5 ? colors.dangerMuted : colors.warningMuted)} title="Views dibagi jumlah subscriber channel">
              <i className="ti ti-target-arrow" style={{ fontSize: 13 }}></i>{v.outlier.toLocaleString('id-ID')}× subscriber
            </span>
          )}
          {v.license === 'creativeCommon'
            ? <span style={badge(colors.success, colors.successMuted)} title="Pemilik mengizinkan dipakai ulang dengan mencantumkan sumber (CC BY)"><i className="ti ti-license" style={{ fontSize: 13 }}></i>Creative Commons</span>
            : v.licensed === false
              ? <span style={badge(colors.success, colors.successMuted)} title="Video tidak diklaim partner Content ID. Musik di dalamnya tetap bisa diklaim pihak lain."><i className="ti ti-shield-off" style={{ fontSize: 13 }}></i>Tanpa Content ID</span>
              : v.licensed === true
                ? <span style={badge(colors.textSecondary, colors.bgCardMuted)} title="Video diklaim partner Content ID (label/distributor)"><i className="ti ti-shield-check" style={{ fontSize: 13 }}></i>Content ID</span>
                : null}
          <span style={badge(tier.c, tier.bg)} title={`Perkiraan RPM ±$${v.rpm}/1.000 views`}><i className="ti ti-coin" style={{ fontSize: 13 }}></i>{tier.t}</span>
        </div>
        <button onClick={() => onGeo(v.video_id, 'geo')} title="Lihat detail negara & peringkat"
          style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: 12, color: v.regions && v.regions.length ? colors.accentText : colors.textSecondary }}>
          <i className="ti ti-world" style={{ fontSize: 13, verticalAlign: '-2px' }}></i>{' '}
          {v.regions && v.regions.length > 0
            ? <>Trending di {v.regions.length} negara: {v.regions.slice(0, 8).map(bendera).join(' ')}{v.regions.length > 8 ? ' …' : ''} <u>detail</u></>
            : <>Detail geografi</>}
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'auto' }}>
          <button title={v.saved ? 'Hapus dari Ide tersimpan' : 'Simpan ke Ide tersimpan'} style={{ ...btn, padding: '6px 8px', ...(v.saved ? { background: colors.accentMuted, color: colors.accentText, borderColor: colors.accent } : {}) }} disabled={busy} onClick={() => onSave(v)}>
            <i className={`ti ${v.saved ? 'ti-check' : 'ti-bookmark'}`} style={{ fontSize: 14 }}></i>{v.saved ? 'Tersimpan' : 'Simpan'}
          </button>
          {dnaOn && v.channel_id && <button title="Analisa channel ini di DNA Channel" style={{ ...btn, padding: '6px 8px' }} disabled={busy} onClick={() => onDna(v)}><i className="ti ti-dna" style={{ fontSize: 14 }}></i>Acuan DNA</button>}
          <button title="Detail: judul, SEO, tags & audio" aria-label="Detail video" style={{ ...btn, padding: '6px 8px' }} onClick={() => onGeo(v.video_id, 'seo')}><i className="ti ti-list-details" style={{ fontSize: 14 }}></i></button>
          <a href={yt} target="_blank" rel="noreferrer" title="Buka di YouTube" aria-label="Buka di YouTube" style={{ ...btn, textDecoration: 'none', padding: '6px 8px' }}><i className="ti ti-external-link" style={{ fontSize: 14 }}></i></a>
        </div>
      </div>
    </div>
  );
}

function NicheSummary({ niche }) {
  const [open, setOpen] = useState(false);
  const s = niche && niche.summary;
  if (!s) return null;
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 14, marginBottom: 14 }}>
      <button onClick={() => setOpen(!open)} style={{ ...btn, border: 'none', padding: 0, background: 'transparent', width: '100%', justifyContent: 'space-between', fontSize: 13 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-sparkles" style={{ fontSize: 16, color: colors.accent }}></i>Pola niche {niche.label} minggu ini</span>
        <i className={`ti ${open ? 'ti-chevron-up' : 'ti-chevron-down'}`}></i>
      </button>
      {open && (
        <div style={{ fontSize: 13, color: colors.textPrimary, marginTop: 10, display: 'grid', gap: 8 }}>
          {s.polaJudul?.length > 0 && <div><b>Pola judul</b><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{s.polaJudul.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {s.durasi && <div><b>Durasi</b> — {s.durasi}</div>}
          {s.jamTayang && <div><b>Jam tayang</b> — {s.jamTayang}</div>}
          {s.bahasa && <div><b>Pasar/bahasa</b> — {s.bahasa}</div>}
          {s.thumbnailJudul && <div><b>Kata untuk thumbnail</b> — {s.thumbnailJudul}</div>}
          {s.saran?.length > 0 && <div><b>Saran untuk channel kecil</b><ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{s.saran.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          <div style={{ fontSize: 11, color: colors.textMuted }}>Dirangkum AI dari {s.sampel} video · {niche.summary_at ? new Date(niche.summary_at).toLocaleDateString('id-ID') : ''}</div>
        </div>
      )}
    </div>
  );
}

export default function Trending({ dnaOn, onOpenDna, ugcOn, onOpenUgc }) {
  const [tab, setTab] = useState('niche'); // niche | chart | saved
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ niche: 'all', lang: 'all', region: 'all', cat: '0', format: 'all', age: '0', sort: 'laju', q: '', cid: 'all' });
  const [qInput, setQInput] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [geo, setGeo] = useState(null); // { id, tab }

  useEffect(() => { axios.get(`${API}/api/trending/meta`).then(r => setMeta(r.data)).catch(() => {}); }, []);
  const loadSaved = useCallback(() => axios.get(`${API}/api/trending/saved`).then(r => setSaved(r.data)).catch(() => {}), []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const load = useCallback(async (offset = 0) => {
    if (tab === 'saved') return;
    setLoading(true);
    try {
      const params = { mode: tab, format: f.format, age: f.age, sort: f.sort, q: f.q, cid: f.cid, limit: PAGE, offset };
      if (tab === 'chart') Object.assign(params, { region: f.region, cat: f.cat });
      else Object.assign(params, { niche: f.niche, lang: f.lang });
      const r = await axios.get(`${API}/api/trending/videos`, { params });
      setData(prev => offset ? { ...r.data, items: [...prev.items, ...r.data.items] } : r.data);
    } catch (e) { /* toast global menampilkan error */ }
    setLoading(false);
  }, [tab, f]);
  useEffect(() => { load(0); }, [load]);

  // Ketik di kolom cari → tunggu sebentar sebelum memuat ulang.
  useEffect(() => { const t = setTimeout(() => setF(p => (p.q === qInput ? p : { ...p, q: qInput })), 400); return () => clearTimeout(t); }, [qInput]);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const gantiTab = (t) => {
    setTab(t);
    setF(p => ({ ...p, sort: (SORT_OPTS[t] || []).some(([k]) => k === p.sort) ? p.sort : 'laju', age: t === 'chart' ? '0' : p.age }));
    if (t === 'saved') loadSaved();
  };
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const toggleSave = async (v) => {
    setBusy(true);
    try {
      if (v.saved) await axios.delete(`${API}/api/trending/saved/${v.video_id}`);
      else await axios.post(`${API}/api/trending/saved`, { video_id: v.video_id });
      setData(p => ({ ...p, items: p.items.map(x => x.video_id === v.video_id ? { ...x, saved: !v.saved } : x) }));
      loadSaved();
      flash(v.saved ? 'Ide dihapus dari daftar.' : 'Disimpan ke Ide tersimpan.');
    } catch (e) { /* toast global */ }
    setBusy(false);
  };
  const unsave = async (id) => {
    setBusy(true);
    try { await axios.delete(`${API}/api/trending/saved/${id}`); setSaved(s => s.filter(x => x.video_id !== id)); setData(p => ({ ...p, items: p.items.map(x => x.video_id === id ? { ...x, saved: false } : x) })); } catch (e) { /* toast */ }
    setBusy(false);
  };
  const saveNote = async (id, note) => { try { await axios.patch(`${API}/api/trending/saved/${id}`, { note }); } catch (e) { /* toast */ } };

  const toDna = async (v) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/dna`, { url: `https://www.youtube.com/channel/${v.channel_id}` });
      flash(`Channel "${v.channel_title}" sedang dianalisa di DNA Channel.`);
      if (onOpenDna) setTimeout(onOpenDna, 1200);
    } catch (e) {
      if (e.response?.status === 409) { flash('Channel ini sudah ada di DNA Channel kamu.'); if (onOpenDna) setTimeout(onOpenDna, 1200); }
    }
    setBusy(false);
  };

  const niches = meta?.niches || [];
  const nicheSel = niches.find(n => n.key === f.niche);
  const regions = (meta?.regions || []).map(r => ({ ...r, nama: namaNegara(r.code, r.name) })).sort((a, b) => a.nama.localeCompare(b.nama, 'id'));
  const regionSel = regions.find(r => r.code === f.region);

  const tabBtn = (id, icon, text) => (
    <button key={id} onClick={() => gantiTab(id)}
      style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${tab === id ? colors.accent : colors.border}`, background: tab === id ? colors.accentMuted : colors.bgCard, color: tab === id ? colors.accentText : colors.textSecondary, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 16 }}></i>{text}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Trending Global</h1>
          <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}>
            Video YouTube yang sedang naik di semua negara, semua kategori, dan niche global. Pakai untuk ide konten, cari pasar, dan channel acuan.
            {meta?.lastRun?.finished_at && <> Diperbarui {umur(meta.lastRun.finished_at)}.</>}
          </div>
        </div>
        <button style={btn} onClick={() => setShowInfo(!showInfo)}><i className="ti ti-info-circle" style={{ fontSize: 15 }}></i>Cara membaca</button>
      </div>

      {geo && <GeoPanel key={geo.id + geo.tab} videoId={geo.id} tabAwal={geo.tab} categories={meta?.categories} onClose={() => setGeo(null)} ugcOn={ugcOn} onOpenUgc={onOpenUgc} />}

      {showInfo && (
        <div style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: 14, fontSize: 13, color: colors.textPrimary, marginBottom: 14, display: 'grid', gap: 6 }}>
          <div><i className="ti ti-rocket" style={{ color: colors.accent }}></i> <b>+…/jam</b> — pertambahan views per jam. Tanda * berarti video baru terlihat sekali, jadi angkanya rata-rata sejak tayang.</div>
          <div><i className="ti ti-target-arrow" style={{ color: colors.danger }}></i> <b>×subscriber</b> — views dibagi subscriber. Angka besar dari channel kecil = formula yang bisa ditiru, bukan sekadar artis besar.</div>
          <div><i className="ti ti-coin" style={{ color: colors.success }}></i> <b>Pasar</b> — perkiraan nilai iklan negara/bahasa penontonnya (tinggi: AS, Inggris, Kanada, Australia, Jerman, dst). Perkiraan kasar, bukan pendapatan pasti.</div>
          <div><b>SHORTS</b> — durasi maksimal 3 menit dan bentuk pemutarnya tegak/persegi. Tanda tanya (SHORTS?) berarti bentuk pemutarnya belum diketahui, jadi masih tebakan dari durasi.</div>
          <div><i className="ti ti-shield-off" style={{ color: colors.success }}></i> <b>Tanpa Content ID</b> — video tidak diklaim partner Content ID (biasanya channel independen, bukan label/distributor). Gabungkan dengan urutan <b>Channel kecil meledak</b> untuk cari formula channel independen yang sedang naik. <b>Bukan jaminan bebas hak cipta</b>: musik atau klip di dalamnya tetap bisa milik orang lain dan diklaim saat diunggah ulang. Hanya <b>Creative Commons</b> yang memang diizinkan pemiliknya untuk dipakai ulang (cantumkan sumber).</div>
          <div style={{ color: colors.warning }}><i className="ti ti-alert-triangle"></i> Jangan unggah ulang video orang lain. Musik, film, dan acara TV hampir semuanya dijaga Content ID. Tiru pola (judul, durasi, jam tayang, gaya thumbnail), bukan kontennya.</div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {tabBtn('niche', 'ti-flame', 'Niche global')}
        {tabBtn('chart', 'ti-world', 'Trending per negara')}
        {tabBtn('saved', 'ti-bookmark', saved.length ? `Ide tersimpan (${saved.length})` : 'Ide tersimpan')}
      </div>

      {msg && <div style={{ background: colors.successMuted, color: colors.success, fontSize: 13, fontWeight: 600, padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      {meta && !meta.lastRun && (
        <div style={{ background: colors.accentMuted, border: `1px solid ${colors.accent}`, color: colors.accentText, borderRadius: radius.lg, padding: 14, fontSize: 13, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <i className="ti ti-clock" style={{ fontSize: 18, flexShrink: 0 }}></i>
          <div><b>Data pertama sedang disiapkan.</b> Video trending dari semua negara dan niche dikumpulkan otomatis tiap 6 jam. Begitu putaran pertama selesai, daftar di bawah langsung terisi.</div>
        </div>
      )}

      {tab !== 'saved' && (
        <>
          <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 14, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {tab === 'niche' ? (
              <>
                <Field title="Niche">
                  <select style={sel} value={f.niche} onChange={e => upd('niche', e.target.value)}>
                    <option value="all">Semua niche</option>
                    {[...new Set(niches.map(n => n.grup || 'Lainnya'))].map(g => (
                      <optgroup key={g} label={g}>
                        {niches.filter(n => (n.grup || 'Lainnya') === g).map(n => <option key={n.key} value={n.key}>{n.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                <Field title="Bahasa">
                  <select style={sel} value={f.lang} onChange={e => upd('lang', e.target.value)}>
                    <option value="all">Semua bahasa</option>
                    {(meta?.langs || []).map(l => <option key={l.lang} value={l.lang}>{namaBahasa(l.lang)} ({l.n})</option>)}
                  </select>
                </Field>
              </>
            ) : (
              <>
                <Field title="Negara">
                  <select style={sel} value={f.region} onChange={e => upd('region', e.target.value)}>
                    <option value="all">🌍 Semua negara</option>
                    {regions.map(r => <option key={r.code} value={r.code}>{bendera(r.code)} {r.nama}</option>)}
                  </select>
                </Field>
                <Field title="Kategori">
                  <select style={sel} value={f.cat} onChange={e => upd('cat', e.target.value)}>
                    {(meta?.categories || [{ id: '0', label: 'Semua kategori' }, { id: '10', label: 'Musik' }]).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
              </>
            )}
            <Field title="Format">
              <select style={sel} value={f.format} onChange={e => upd('format', e.target.value)}>
                <option value="all">Semua</option>
                <option value="short">Shorts</option>
                <option value="long">Video panjang</option>
              </select>
            </Field>
            <Field title="Content ID">
              <select style={sel} value={f.cid} onChange={e => upd('cid', e.target.value)}>
                <option value="all">Semua</option>
                <option value="none">Tanpa Content ID</option>
                <option value="yes">Ber-Content ID</option>
                <option value="cc">Creative Commons</option>
              </select>
            </Field>
            <Field title="Tayang">
              <select style={sel} value={f.age} onChange={e => upd('age', e.target.value)}>
                <option value="0">Kapan saja</option>
                <option value="24">24 jam terakhir</option>
                <option value="48">48 jam terakhir</option>
                <option value="168">7 hari terakhir</option>
              </select>
            </Field>
            <Field title="Urutkan">
              <select style={sel} value={f.sort} onChange={e => upd('sort', e.target.value)}>
                {(SORT_OPTS[tab] || []).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </Field>
            <Field title="Cari judul / channel">
              <input type="search" value={qInput} onChange={e => setQInput(e.target.value)} placeholder="mis. asmr, minecraft, phonk"
                style={{ ...sel, width: '100%', boxSizing: 'border-box' }} />
            </Field>
          </div>

          {tab === 'niche' && <NicheSummary niche={nicheSel} />}
          {tab === 'chart' && regionSel && regionSel.music_ok === false && f.cat === '10' && (
            <div style={{ fontSize: 13, color: colors.warning, marginBottom: 12 }}>YouTube tidak menyediakan chart Musik untuk {regionSel.nama}. Pilih "Semua kategori".</div>
          )}

          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 10 }}>{loading && !data.items.length ? 'Memuat…' : `${data.total.toLocaleString('id-ID')} video`}</div>
          {!loading && data.total === 0 && meta?.lastRun && (
            <div style={{ background: colors.bgCard, border: `1px dashed ${colors.borderStrong}`, borderRadius: radius.lg, padding: 30, textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>
              Belum ada video yang cocok. Coba longgarkan filter (format, waktu tayang, atau bahasa).
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
            {data.items.map(v => <VideoCard key={v.video_id} v={v} mode={tab} onSave={toggleSave} onDna={toDna} dnaOn={dnaOn} busy={busy} onGeo={(id, t) => setGeo({ id, tab: t || 'seo' })} />)}
          </div>
          {data.items.length < data.total && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button style={{ ...btn, padding: '9px 18px' }} disabled={loading} onClick={() => load(data.items.length)}>{loading ? 'Memuat…' : 'Muat lebih banyak'}</button>
            </div>
          )}
        </>
      )}

      {tab === 'saved' && (
        saved.length === 0 ? (
          <div style={{ background: colors.bgCard, border: `1px dashed ${colors.borderStrong}`, borderRadius: radius.lg, padding: 30, textAlign: 'center', color: colors.textSecondary, fontSize: 13 }}>
            Belum ada ide tersimpan. Tekan <b>Simpan ide</b> di kartu video untuk mengumpulkan referensi.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {saved.map(s => (
              <div key={s.video_id} style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href={`https://www.youtube.com/watch?v=${s.video_id}`} target="_blank" rel="noreferrer" style={{ flex: '0 0 160px', maxWidth: '100%', aspectRatio: '16 / 9', background: colors.bgCardMuted, borderRadius: 8, overflow: 'hidden' }}>
                  {s.thumbnail && <img src={s.thumbnail} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </a>
                <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{s.title || s.video_id}</div>
                  <div style={{ fontSize: 12, color: colors.textSecondary }}>{s.channel_title}{s.views != null ? ` · ${fmt(s.views)} views` : ''}{s.duration_sec != null ? ` · ${dur(s.duration_sec)}` : ''} · disimpan {umur(s.saved_at)}</div>
                  <textarea defaultValue={s.note || ''} placeholder="Catatan: apa yang mau ditiru (judul, durasi, thumbnail, jam tayang)…" rows={2}
                    onBlur={e => { if ((e.target.value || '') !== (s.note || '')) saveNote(s.video_id, e.target.value); }}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: 8, borderRadius: 8, border: `1px solid ${colors.border}`, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={btn} onClick={() => setGeo({ id: s.video_id, tab: 'seo' })}><i className="ti ti-list-details" style={{ fontSize: 14 }}></i>Detail (SEO, audio)</button>
                    {dnaOn && s.channel_id && <button style={btn} disabled={busy} onClick={() => toDna(s)}><i className="ti ti-dna" style={{ fontSize: 14 }}></i>Acuan DNA</button>}
                    <button style={{ ...btn, color: colors.danger }} disabled={busy} onClick={() => unsave(s.video_id)}><i className="ti ti-trash" style={{ fontSize: 14 }}></i>Hapus</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
